<template>
    <div>
        <Player
            ref="player"
            :play-item="props.playItem"
            :play-item-type="props.playItemType"
            @exit-play="onExitPlay"
            @show-subtitle-modal="showSubtitleModal"
            @set-visibility-to-btn-next-episode="setVisibilityToBtnNextEpisode"
            @show-controls="showControlPanel"
            @set-visibility-to-btn-show-replay="setVisibilityToBtnShowReplay"
            @show-retry-modal="showRetryModal"
            @emulate-key-enter="emulateKeyEnter"
        />
    </div>
</template>

<script setup lang="ts">
    import  Player from "@/components/Player/Player.vue";
    import { type PropType, onMounted, onUnmounted, type Ref, ref, nextTick, watch } from "vue";
    import type { PlayerRef } from "@/components/Player/types";
    import { type PlayItem, isTabletDevice } from "@ca.vmedia.tv/domains";
    import { useRouter, onBeforeRouteLeave } from 'vue-router';
    import { useParentControlStore } from "@/stores/ParentControl";
    import { storeToRefs } from 'pinia';
    import { usePlayerStore } from "@/stores/Player";
    import { useClientMessagesStore } from "@/stores/ClientMessages";
    import { useApplicationStore } from "@/stores/Application";

    const ClientMessagesStore = useClientMessagesStore();
    const {
        isModalVisible: isModalMessagesShown,
    } = storeToRefs(ClientMessagesStore);

    const ParentControlStoreModule = useParentControlStore();
    const {
        getIsModalParentVisible: isTypeModeEnabledPin
    } = storeToRefs(ParentControlStoreModule);

    const PlayerStoreModule = usePlayerStore();
    const {
        isAlertModalVisible
    } = storeToRefs(PlayerStoreModule);

    const ApplicationStore = useApplicationStore();
    const {
        getIsGlobalMaintenanceModalShow,
        getIsInternetModalShow,
    } = storeToRefs(ApplicationStore);

    const props = defineProps({
        playItem: {
            type: Object as PropType<PlayItem>,
            required: true
        },
        playItemType: {
            type: String,
            required: true
        },
    });
    const router = useRouter();
    const emit = defineEmits(['showSubtitleModal']);
    const exitPlayer: Ref<boolean> = ref(false);
    const showModal: Ref<boolean> = ref(false);
    const isVisibleBtnNextEpisode: Ref<boolean> = ref(false);
    const isShowingControls: Ref<boolean> = ref(false);
    const isVisibleBtnShowReplay: Ref<boolean> = ref(false);
    const isShowingRetryModal: Ref<boolean> = ref(false);
    const player: Ref<PlayerRef | null> = ref(null);


    const setVisibilityToBtnNextEpisode = (val: boolean) => {
        isVisibleBtnNextEpisode.value = val;
    };
    const showControlPanel = (val: boolean) => {
        isShowingControls.value = val;
    };
    const setVisibilityToBtnShowReplay = (val: boolean) => {
        isVisibleBtnShowReplay.value = val;
    };
    const showRetryModal = (val: boolean) => {
        isShowingRetryModal.value = val;
    };
    const showSubtitleModal = (val: boolean) => {
        showModal.value = val;
        emit("showSubtitleModal",showModal.value);
    };
    const showControls = () => {
        player.value?.toggleShowControls();
    };
    const hideControls = () => {
        player.value?.onHide();
    };
    const goNextEpisode = () => {
        player.value?.goNextEpisode();
    };
    const goPrevEpisode = () => {
        player.value?.goPrevEpisode();
    };
    const clickBtnsReplay = () => {
        player.value?.clickBtnsReplay();
    };
    const onRewind = () => {
        showControls();
        void nextTick(() => {
            void nextTick(() => {
                player.value?.onRewind();
            });
        });
    };
    const onPlayPressed = () => {
        showControls();
        void nextTick(() => {
            player.value?.onPlayPressed();
        });
    };
    const onPausePressed = () => {
        showControls();
        void nextTick(() => {
            player.value?.onPausePressed();
        });
    };
    const onFastForward = () => {
        showControls();
        void nextTick(() => {
            void nextTick(() => {
                player.value?.onFastForward();
            });
        });
    };
    const onLeftPressed = () => {
        if(isVisibleBtnShowReplay.value || isTypeModeEnabledPin.value || getIsInternetModalShow.value) {
            return;
        }

        showControls();
        void nextTick(() => {
            void nextTick(() => {
                player.value?.onLeftPressed();
            });
        });
    };
    const onRightPressed = () => {
        if(isVisibleBtnShowReplay.value || isVisibleBtnNextEpisode.value || isTypeModeEnabledPin.value || getIsInternetModalShow.value) {
            return;
        }

        showControls();
        void nextTick(() => {
            void nextTick(() => {
                player.value?.onRightPressed();
            });
        });
    };
    const onExitPlay = () => {
        exitPlayer.value = true;
        router.go(-1);
    };

    const emulateKeyEnter = () => {
        const keyEvent = new KeyboardEvent("keyup", { key: "Enter", keyCode: 13});
        document.body.dispatchEvent(keyEvent);
    };
    const handleTouchStartEvents = (event: TouchEvent): void => {
        const target = event.target as HTMLElement;
        if (target.id === "guideControlsPannel" || target.id === "guideControlsPannelContainer") {
            hideControls();
        }

        if (target.localName === "video") {
            emulateKeyEnter();
        }
    };
    const onUpDownPressed = (): void => {
        if (!showModal.value && !isTypeModeEnabledPin.value && !isShowingControls.value && !getIsInternetModalShow.value) {
            showControls();
        }
    };
    const handleKeyEvents = (event: KeyboardEvent): void => {
        if (isModalMessagesShown.value || isShowingRetryModal.value || getIsGlobalMaintenanceModalShow.value || isAlertModalVisible.value || getIsInternetModalShow.value) {
            return;
        }
        
        switch (event.keyCode) {
            case 13: { // ok
                if (!showModal.value && !isTypeModeEnabledPin.value && !isVisibleBtnNextEpisode.value) {
                    showControls();
                } else if (!showModal.value && !isTypeModeEnabledPin.value && isVisibleBtnNextEpisode.value && !isShowingControls.value) {
                    goNextEpisode();
                } else if (!showModal.value && !isTypeModeEnabledPin.value && isVisibleBtnShowReplay.value && !isShowingControls.value) {
                    clickBtnsReplay();
                }
                break;
            }
            case 38: { // up
                onUpDownPressed();
                break;
            }
            case 40: { // down
                onUpDownPressed();
                break;
            }
            case 415: { // play
                onPlayPressed();
                break;
            }
            case 19: { // pause
                onPausePressed();
                break;
            }
            case 37: { // left
                onLeftPressed();
                break;
            }
            case 39: { // right
                onRightPressed();
                break;
            }
            case 412: { // rewind
                onRewind();
                break;
            }
            case 417: { // fastforward
                onFastForward();
                break;
            }
            case 10232: { // MediaTrackPrevious
                showControls();
                goPrevEpisode();
                break;
            }
            case 10233: { // MediaTrackNext
                showControls();
                goNextEpisode();
                break;
            }
            case 413: { // stop
                router.go(-1);
                break;
            }
            case 33: // WebOs channel +
            case 427: { // Tizen channel +
                player.value?.onNextChannelPressed();
                break;
            }
            case 34: // WebOs channel -
            case 428: // Tizen channel -
            case 10190: { // Tizen previous channel
                player.value?.onPrevChannelPressed();
                break;
            }
        }
    };

    onMounted(() => {
        document.addEventListener("keyup", handleKeyEvents, true);
        if (isTabletDevice) {
            document.addEventListener("touchstart", handleTouchStartEvents);
        }
    });

    onBeforeRouteLeave((_to, _from, next) => {
        if (exitPlayer.value) {
            next();
        } else {
            player.value?.confirmStop();
            next(false);
        }
    });

    onUnmounted(() => {
        document.removeEventListener("keyup", handleKeyEvents, true);

        if (isTabletDevice) {
            document.removeEventListener("touchstart", handleTouchStartEvents);
        }
    });
    watch(isModalMessagesShown, () => {
        player.value?.onPlay();
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>